<template>
    <v-dialog
        fullscreen
        hide-overlay
        persistent
        transition="dialog-bottom-transition"
        v-model="openState">
        <div class="dialog fullscreen create-job">
            <!-- Dialog Header -->
            <header class="dialog__header grid-a">
                <div class="header__inner">
                    <div class="title-content grid-c">
                        <h1 class="display-1 header-title">Job Request</h1>
                        <h3 class="header-subtitle">
                            <span v-if="job.status">
                                Status: {{getJobEnumItemValue('jobStatuses', job.status, 'name')}} - <span v-if="!formIsEditable">Not</span> Editable
                            </span>
                        </h3>
                    </div>
                    <div class="close-btn grid-d">
                        <v-btn
                            dark fab small
                            depressed flat
                            @click.stop="cancelJob">
                            <v-icon size="28">close</v-icon>
                        </v-btn>
                    </div>
                </div>
            </header>

            <!-- Dialog Content -->
            <section
                    ref="dialogContent"
                    class="dialog__content">
                <div class="content__inner">
                    <div class="section content__job-overview">
                        <div class="section__title">
                            <h3 class="headline">Job Overview</h3>
                            <p>Enter the core details of your job request.</p>
                        </div>
                        <div class="section__content">
                            <div class="subsection">
                                <div class="subsection__title">
                                    <h4 class="title">Description</h4>
                                </div>
                                <div class="subsection__content">
                                    <div class="subgroup">
                                        <v-text-field
                                            box
                                            counter
                                            :disabled="!formIsEditable && newJobData.state !== 'draft'"
                                            :error="!$v.requestName.required && submitted"
                                            maxlength="60"
                                            label="Job Name"
                                            v-model="requestName"/>

                                        <p>
                                            <span class="rules dont"/> include information that is already visible like,
                                            Rush, Dept, Job Code, submit or due dates.
                                            <span class="rules dont"/> use all caps. Simplified season, month or year is
                                            ok.
                                        </p>

                                        <div class="alerts">
                                            <div class="alert">
                                                <v-icon size="18" color="red darken-4">cancel</v-icon>
                                                <span>RUSH - INTL - ITALY - BIBIONE BEACH FITNESS EXPO EVENT - M & W SPORT - JULY 2018</span>
                                            </div>
                                            <div class="alert">
                                                <v-icon size="18" color="green darken-3">check_circle</v-icon>
                                                <span>Italy Bibione Beach Fitness Expo Event - M & W Sport Jul'18</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="subgroup description-selects">
                                        <div class="dept-select grid-e">
                                            <v-select
                                                box dense
                                                ref="department-select"
                                                label="Department"
                                                item-text="name"
                                                item-value="value"
                                                :disabled="requestDepartmentIsDisabled || (!formIsEditable && newJobData.state !== 'draft')"
                                                :items="getScopedDepartments"
                                                v-model="requestDept"/>
                                        </div>
                                        <div class="type-select grid-f">
                                            <v-select
                                                box dense
                                                label="Type"
                                                item-text="name"
                                                item-value="value"
                                                :disabled="(!formIsEditable && newJobData.state !== 'draft')"
                                                :items="$store.state.properties.jobTypes"
                                                v-model="requestType"/>
                                            <v-text-field
                                                v-if="!isNewBox && requestType === $store.state.constants.SILA_JOB_TYPE_PACKAGING"
                                                box
                                                :disabled="!(formIsEditable && isSaved)"
                                                label='Running Change Job ID'
                                                hint='Enter the full ID for the previous job ie: "PKG36404"'
                                                persistent-hint
                                                maxlength="10"
                                                :error="!$v.requestJobNumber.jobNumberFormat"
                                                v-model="requestJobNumber"/>
                                            <v-checkbox
                                                v-if="requestType === $store.state.constants.SILA_JOB_TYPE_PACKAGING"
                                                :height="10"
                                                v-model="isNewBox"
                                                color="primary"
                                                :disabled="getGenericButtonsDisabled"
                                                label="Is New Box?"
                                            />
                                        </div>

                                        <!-- <div v-if="getMatchingEnum('jobDepartments', requestDept, 'International')">
                                            <v-radio-group
                                                row
                                                v-model="requestDeptOptions">
                                                <v-radio
                                                    v-for="item in jobInternationalSubdepartments"
                                                    :key="generateUniqueHash(item)"
                                                    :label="item.name"
                                                    :value="item.value"/>
                                            </v-radio-group>
                                        </div> -->
                                    </div>


                                    <div class="subgroup">
                                        <div class="creatable-html-editor">
                                            <div :class="[{ 'creatable-disable': !(formIsEditable && isSaved) }]">
                                                <div class="subsection__title">
                                                    <h4 class="title">Short Description</h4>
                                                </div>
                                                <div id="requestDescription" @paste="pasteValues('DESCRIPTION')">
                                                    <html-editor
                                                        :error="(!$v.requestDescription.notHtmlEditorDefault && submitted) || (descriptionCount > 5000)"
                                                        v-model="requestDescription"
                                                        filter="simple"
                                                        :max-length="5000"
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                class="d-flex justify-space-between"
                                            >
                                                <div class="creatable-hint">
                                                    Use this field to briefly describe what the job is. Max 250 characters
                                                </div>
                                                <div class="creatable-counter">{{descriptionCount}}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                    v-if="milestones.length"
                                    :class="['subsection', {disabled:!(formIsEditable && isSaved)}]">
                                <div class="subsection__title">
                                    <h4 class="title">Schedule</h4>
                                    <p>Dates will be calculated upon approval. Number of days below represent working days only.</p>
                                </div>
                                <div class="subsection__content">
                                    <div class="schedule-milestones">
                                        <job-milestones
                                            :milestones="milestones"
                                            show-offset
                                            type="create"/>
                                    </div>
                                    <!-- <v-switch
                                        label="24hr Rush"
                                        :disabled="!canBe24Hr"
                                        v-model="request24Hr"/> -->
                                </div>
                            </div>
                            <div
                                    v-if="milestones.length"
                                    :class="['subsection', {disabled:!(formIsEditable && isSaved)}]">
                                <div class="subsection__title">
                                    <h4 class="title">Special Schedule Request</h4>
                                    <p v-html="rushNote"></p>
                                </div>
                                <div class="subsection__content">
                                    <div class="subgroup special-schedule-request">
                                        <v-radio-group
                                                row
                                                v-model="requestPriority">
                                            <v-radio
                                                v-for="(item, index) in jobPriorities"
                                                :key="index"
                                                :label="item.name"
                                                :value="item.value"/>
                                        </v-radio-group>

                                        <v-radio-group
                                            row
                                            v-model="request24Hr">
                                            <v-radio
                                                label="24hr Rush"
                                                :value="true"
                                                :disabled="!canBe24Hr"/>
                                        </v-radio-group>

                                    </div>
                                    <div class="subgroup special-request-fields">
                                        <div
                                            v-if="getMatchingEnum('jobPriorities', requestPriority, 'Rush')"
                                            class="field-group">
                                            <!--
                                            <div class="grid-half">
                                                <v-menu
                                                    lazy full-width
                                                    :close-on-content-click="false"
                                                    v-model="requestedFirstComp_dateMenuState"
                                                    transition="slide-y-transition"
                                                    max-width="290px"
                                                    min-width="290px">
                                                    <v-text-field
                                                        box readonly
                                                        persistent-hint
                                                        :error="!$v.requestDateRequestedFirstComp.required && submitted"
                                                        slot="activator"
                                                        hint="Both rush dates are required"
                                                        label="Requested First Comp Date"
                                                        append-icon="calendar_today"
                                                        v-model="requestDateRequestedFirstComp_String">
                                                    </v-text-field>
                                                    <v-date-picker
                                                        no-title
                                                        :allowed-dates="getAllowedDates"
                                                        :min="today"
                                                        @input="requestedFirstComp_dateMenuState = false"
                                                        v-model="requestDateRequestedFirstComp">
                                                    </v-date-picker>
                                                </v-menu>
                                            </div>
                                            -->
                                            <div class="grid-full">
                                                <v-menu
                                                    lazy full-width
                                                    :close-on-content-click="false"
                                                    v-model="requestedDelivery_dateMenuState"
                                                    transition="slide-y-transition"
                                                    max-width="290px"
                                                    min-width="290px">
                                                    <v-text-field
                                                        box readonly
                                                        persistent-hint
                                                        :error="!$v.requestDateRequestedDelivery.required && submitted"
                                                        slot="activator"
                                                        hint="Requested Delivery Date is required"
                                                        label="Requested Delivery Date"
                                                        append-icon="calendar_today"
                                                        v-model="requestDateRequestedDelivery_String">
                                                    </v-text-field>
                                                    <v-date-picker
                                                        no-title
                                                        :allowed-dates="getAllowedDates"
                                                        :min="today"
                                                        @input="requestedDelivery_dateMenuState = false"
                                                        v-model="requestDateRequestedDelivery">
                                                        <!-- <v-spacer></v-spacer> -->
                                                    </v-date-picker>
                                                </v-menu>
                                            </div>
                                            <div class="grid-full" style="margin-top: 10px">
                                                <p>All rush jobs require proof of Director-level approval. Please provide a brief description of why a rush schedule is required. Upload proof of director approval to Project Files. You may be contacted with any questions.</p>
                                                <p>Rush schedule request is subject to approval by Advertising VP.</p>
                                            </div>
                                            <div class="grid-half">
                                                <v-text-field
                                                    box label="Name of Director"
                                                    :error="!$v.requestSupervisorName.required && submitted"
                                                    v-model="requestSupervisorName"/>
                                            </div>
                                            <div class="grid-half">
                                                <v-text-field
                                                    box label="Reason for Rush"
                                                    :error="!$v.requestRushReason.required && submitted"
                                                    v-model="requestRushReason"/>
                                            </div>
                                            <div class="grid-full approval-box">
                                                <!-- uses the same file uploader component below -->
                                                <div
                                                    :class="['add-files-btn', {'drag-over': uploadDragFile}, {'not-complete': clickedSubmitJobOnce && fileAttachmentsJustApprovals.length === 0}]"
                                                    @dragover.prevent
                                                    @dragleave.prevent.stop="uploadDragOver(false)"
                                                    @dragenter.prevent.stop="uploadDragOver(true)"
                                                    @drop.prevent.stop="dropFiles"
                                                    @click="addJobAttachment(
                                                    {
                                                        uploadFileOnly: true,
                                                        showEditCompRound: false,
                                                        filteredUploadItemSubtypesJustThese: [
                                                            'Proof of Approval'
                                                        ]
                                                    })">
                                                    <add-item-button
                                                        centered
                                                        size="large"
                                                        label="Click or Drag to Add Signed Approval"/>
                                                    <div class="btn--scrim"/>
                                                </div>
                                                <div
                                                    v-if="fileAttachmentsJustApprovals.length > 0"
                                                    class="attachments-container--inner">
                                                    <attachment-file-item
                                                        v-for="(file, index) in fileAttachmentsJustApprovals"
                                                        :key="`attched-file-${index}-${job.id}`"
                                                        :data="file"
                                                        :removeDispatchEvent="removeFileAttachments"/>
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                                v-if="getMatchingEnum('jobPriorities', requestPriority, 'Extended')"
                                                class="field-group">
                                            <div class="grid-half">
                                                <v-menu
                                                    lazy full-width
                                                    :close-on-content-click="false"
                                                    v-model="requestedDelivery_dateMenuState"
                                                    transition="slide-y-transition"
                                                    max-width="290px"
                                                    min-width="290px">
                                                    <v-text-field
                                                        box readonly
                                                        persistent-hint
                                                        slot="activator"
                                                        :error="!$v.requestDateRequestedDelivery.required && submitted"
                                                        label="Requested Delivery Date"
                                                        append-icon="calendar_today"
                                                        v-model="requestDateRequestedDelivery_String">
                                                    </v-text-field>
                                                    <v-date-picker
                                                        no-title
                                                        :allowed-dates="getAllowedDates"
                                                        :min="today"
                                                        @input="requestedDelivery_dateMenuState = false"
                                                        v-model="requestDateRequestedDelivery">
                                                        <v-spacer></v-spacer>
                                                    </v-date-picker>
                                                </v-menu>
                                            </div>
                                        </div>

                                        <div class="rush-24hr-fields">
                                            <div v-if="request24Hr" class="field-group">
                                                <div class="grid-full">
                                                    <p>24hr rush jobs require request by email prior to job submission. Prior to submission, <a target="_blank" :href="`mailto:${getTeamSplitContactsEmails}`">contact the following</a> with reason for 24hr rush, including requesting executive in CC.  You may be contacted for additional details and approval status.</p>
                                                    <p v-html="getTeamSplitContacts"></p>
                                                    <p>Once the rush request is approved by the above contacts, proceed to job submission - include screenshot of approval in the Job Files.</p>
                                                </div>
                                                <div class="grid-half">
                                                    <v-text-field
                                                        box label="Name of Supervisor"
                                                        :error="!$v.requestSupervisorName.required && submitted"
                                                        v-model="requestSupervisorName"/>
                                                </div>
                                                <div class="grid-half">
                                                    <v-text-field
                                                        box label="Reason for Rush"
                                                        :error="!$v.requestRushReason.required && submitted"
                                                        v-model="requestRushReason"/>
                                                </div>
                                                <div class="grid-full approval-box">
                                                    <!-- uses the same file uploader component below -->
                                                    <div
                                                        :class="['add-files-btn', {'drag-over': uploadDragFile}, {'not-complete': clickedSubmitJobOnce && fileAttachmentsJustApprovals.length === 0}]"
                                                        @dragover.prevent
                                                        @dragleave.prevent.stop="uploadDragOver(false)"
                                                        @dragenter.prevent.stop="uploadDragOver(true)"
                                                        @drop.prevent.stop="dropFiles"
                                                        @click="addJobAttachment(
                                                    {
                                                        uploadFileOnly: true,
                                                        showEditCompRound: false,
                                                        filteredUploadItemSubtypesJustThese: [
                                                            'Proof of Approval'
                                                        ]
                                                    })">
                                                        <add-item-button
                                                            centered
                                                            size="large"
                                                            label="Click or Drag to Add Signed Approval"/>
                                                        <div class="btn--scrim"/>
                                                    </div>
                                                    <div
                                                        v-if="fileAttachmentsJustApprovals.length > 0"
                                                        class="attachments-container--inner">
                                                        <attachment-file-item
                                                            v-for="(file, index) in fileAttachmentsJustApprovals"
                                                            :key="`attched-file-${index}-${job.id}`"
                                                            :data="file"
                                                            :removeDispatchEvent="removeFileAttachments"/>
                                                    </div>
                                                </div>
                                            </div>



                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="divider"/>

                    <div class="section content__job-details">
                        <div class="section__title">
                            <h3 class="headline">Job Details</h3>
                            <p>Describe the output requirements.</p>
                        </div>
                        <div class="section__content">
                            <div :class="['subsection', {disabled:!(formIsEditable && isSaved)}]">
                                <div class="subsection__title">
                                    <h4 class="title">Directions*</h4>
                                    <p>Any instructions on how to complete job</p>
                                </div>
                                <div id="requestDirections" @paste="pasteValues('DIRECTION')" class="subsection__content">
                                    <html-editor
                                        :maxLength="10000"
                                        :error="!$v.requestDirections.notHtmlEditorDefault && submitted"
                                        v-model="requestDirections"
                                    />
                                </div>
                            </div>

                            <div :class="['subsection', {disabled:!(formIsEditable && isSaved)}]">
                                <div class="subsection__title">
                                    <h4 class="title">Job Specifications*</h4>
                                    <p>Use this section to give specs for each element included in your job such as
                                        dimensions, file size, print specs, etc</p>
                                </div>
                                <div id="requestSpecifications" @paste="pasteValues('JOB_SPECIFICATION')" class="subsection__content">
                                    <html-editor
                                        :error="!$v.requestSpecifications.notHtmlEditorDefault && submitted"
                                        v-model="requestSpecifications"
                                    />
                                </div>
                            </div>

                            <div :class="['subsection', 'job-details__files', {disabled:!(formIsEditable && isSaved)}]">
                                <div class="subsection__title">
                                    <h4 class="title">Files</h4>
                                    <p>Upload reference files, specs, distros - anything that you've mentioned above
                                        that will help guide creation of elements in your request. You can always add
                                        more files later.</p>
                                </div>
                                <div class="subsection__content">
                                    <core-uploader
                                        multiple
                                        ref="coreUploader"
                                        class="core-uploader"
                                        :attachTo="getAttachTo"
                                        :fileUsageType="fileUsageType"
                                        :dispatch-on-items-selected="dropItemsLoaded"/>
                                    <div
                                        :class="['add-files-btn', {'drag-over': uploadDragFile}]"
                                        @dragover.prevent
                                        @dragleave.prevent.stop="uploadDragOver(false)"
                                        @dragenter.prevent.stop="uploadDragOver(true)"
                                        @drop.prevent.stop="dropFiles"
                                        @click="addJobAttachment(
                                        {
                                            showEditCompRound: false,
                                            filteredUploadItemSubtypes: [
                                                'SILA 1 Comp',
                                                'SILA 1 Project File'
                                            ]
                                        })">
                                        <add-item-button
                                            centered
                                            size="large"
                                            label="Click or Drag to Add Files"/>
                                        <div class="btn--scrim"/>
                                    </div>
                                    <div
                                        v-if="fileAttachments.length > 0"
                                        class="attachments-container--inner">
                                        <attachment-file-item
                                            v-for="(file, index) in fileAttachments"
                                            :key="`attched-file-${index}-${job.id}`"
                                            :data="file"
                                            :removeDispatchEvent="removeFileAttachments"/>
                                    </div>
                                </div>
                            </div>

                            <div :class="['subsection', 'job-details__directions', {disabled:!(formIsEditable && isSaved)}]">
                                <div class="subsection__title">
                                    <h4 class="title">Delivery Instructions*</h4>
                                    <p>Describe any shipping or delivery instructions</p>
                                    <ul
                                        v-if="newJobData.type == $store.state.constants.SILA_JOB_TYPE_MULTIMEDIA">
                                        <li>Frame Rate (i.e. 24fps)</li>
                                        <li>Codec/File type (i.e. MP4)</li>
                                        <li>Duration in seconds (i.e. 30sec)</li>
                                        <li>Size in pixels Width x Height (i.e. 1920x1080)</li>
                                        <li>File size limit (i.e. under 10MB)</li>
                                    </ul>
                                </div>
                                <div id="requestDeliveryInstructions" @paste="pasteValues('DELIVERY')" class="subsection__content">
                                    <html-editor
                                        :error="!$v.requestDeliveryInstructions.notHtmlEditorDefault && submitted"
                                        v-model="requestDeliveryInstructions"
                                    />
                                </div>
                            </div>

                            <delivery-options
                                    :class="['subsection', {disabled:!(formIsEditable && isSaved)}]"
                                    :editable="true"
                                    :type="newJobData.type"
                                    :department="newJobData.department"
                                    dataSet="jobCreate"/>
                        </div>
                    </div>

                    <div class="divider"/>

                    <div class="section content__job-premedia">
                        <div class="section__title">
                            <h3 class="headline">Pre-Media</h3>
                            <p>If your job has and shoe swaps, custom shot, or retouching add a row here for each
                                output.</p>
                        </div>
                        <div class="section__content">
                            <div :class="['subsection', {disabled:!(formIsEditable && isSaved)}]">
                                <div class="subsection__content">

                                    <premedia
                                            :can-create="true"
                                            :can-edit="true"
                                            :can-approve="false"
                                            :can-view-approved="false"/>

                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="divider"/>

                    <div :style="(premediaGroupIsEditing) ? 'opacity:0.4':''" class="section content__job-actions">
                        <div class="section__content">
                            <div
                                    v-if="submitted && !isFormValid"
                                    class="form-error-message">Please fix all form error and resubmit
                            </div>
                            <v-btn
                                    depressed
                                    color="primary"
                                    :disabled="getGenericButtonsDisabled"
                                    :loading="createJobStatus.isSaving"
                                    @click="submitJob"
                                    >{{isResubmit}}</v-btn>
                            <v-btn
                                    :outline="!getSaveButtonDisabled"
                                    color="grey darken-1"
                                    class="white--background"
                                    :disabled="getSaveButtonDisabled"
                                    :loading="createJobStatus.isSaving"
                                    @click="saveJob">Save {{ saveDeleteButtonLabel }}
                            </v-btn>
                            <v-btn
                                    :outline="formIsEditable"
                                    color="red darken-4"
                                    :disabled="getGenericButtonsDisabled"
                                    :loading="createJobStatus.isSaving"
                                    @click="deleteJob">Delete {{ saveDeleteButtonLabel }}
                            </v-btn>
                            <v-btn
                                    :outline="formIsEditable"
                                    color="red darken-4"
                                    :disabled="getGenericButtonsDisabled"
                                    :loading="createJobStatus.isSaving"
                                    @click="archiveJob">Archive {{ saveDeleteButtonLabel }}
                            </v-btn>
                        </div>
                    </div>
                </div>

            </section>

            <div
                v-if="isSaving"
                class="loading-overlay">
                <div class="overlay__message">
                    <v-progress-circular
                        indeterminate
                        color="white"/>
                        <div class="message__note">
                        <span>Auto-saving Draft...</span>
                    </div>
                </div>
            </div>
        </div>
    </v-dialog>
</template>


<script>
    import dayjs from 'dayjs'
    import debounce from 'lodash.debounce';
    import {helpers, required, minLength } from 'vuelidate/lib/validators'
    import {mapState, mapGetters, mapActions, mapMutations} from 'vuex'
    import stripHtml from "../../../helpers/stripHtml"

    const DELAY = 800

    import {
        VUEX_JOB_CREATE_UPDATE,
        VUEX_JOB_CREATE_CANCEL,
        VUEX_JOB_CREATE_CLEAR_DELIVERY_OPTIONS,
        VUEX_JOB_TRANSITION,
        VUEX_JOB_CREATE_INIT_MILESTONES_DONE,
        VUEX_JOB_DELETE,
        VUEX_JOB_REMOVE_FILE,
    } from '@/store/constants/jobs'
    import {
        VUEX_ATTACHMENT_QUEUE_MANAGER_INIT_JOB_ATTACHMENT,
        VUEX_ATTACHMENT_QUEUE_MANAGER_REMOVE_FILE
    } from '@/store/constants/attachment_queue_manager'
    import {
        VUEX_ATTACHMENTS_DELETE_ATTACHMENT
    } from '@/store/constants/attachments'

    import {
        MILESTONEDATA
    } from '@/store/modules/config/stub_milestone_data'


    import MilestoneBuilder from '@/js/milestoneBuilder'

    import {
        notHtmlEditorDefault,
        jobNumberFormat
    } from '@/mixins/customValidators'


    import HTMLEditor from '@/components/ui/_global/utils/HTMLEditor/HTMLEditor_Quill.vue'
    import Job_Overview_Milestones from '@/components/ui/Jobs/Job_Overview_Milestones.vue'
    import AddItemButton from '@/components/ui/_global/Add_Item_Button.vue'
    import JobDetailsDeliveryOptions from '@/components/ui/Jobs/Job_Details_Delivery_Options.vue'
    import Premedia from '@/components/ui/Jobs/Job_Premedia'
    import AttachmentFileItem from '@/components/ui/_global/Attachment_File_Item'
    import * as VDatePicker from 'vuetify/es5/components/VDatePicker';
    import CoreUploader from '@/components/ui/_global/uploaders/Core_Uploader'

    export default {
        name: 'create-job',

        components: {
            'html-editor': HTMLEditor,
            'job-milestones': Job_Overview_Milestones,
            'add-item-button': AddItemButton,
            'delivery-options': JobDetailsDeliveryOptions,
            'premedia': Premedia,
            'attachment-file-item': AttachmentFileItem,
            'core-uploader': CoreUploader,
            ...VDatePicker
        },

        data: () => ({
            MB: null,
            milestones: [],
            canBe24Hr: true,
            requestedDelivery_dateMenuState: false,
            requestedFirstComp_dateMenuState: false,
            isFormValid: true,
            submitted: true,
            uploadDragFile: false,
            descriptionCount: null,

            isNewBox:'',//used in Department Packaging, Type Packaging
            clickedSubmitJobOnce: false,
            rushNote: 'Please note – Once comps are posted for rush jobs, please provide feedback ASAP (same day, in some cases). It is the requestor’s responsibility to plan for approvals within the requested rush timeline. Rush timeframes cannot be guaranteed on projects that do not comply.'
        }),

        validations: {
            requestName: {
                required,
                minLength: minLength(1)
            },
            requestDescription: {
                required,
                minLength: minLength(1),
                notHtmlEditorDefault
            },
            // requestDateRequestedFirstComp: {
            //     required,
            //     minLength: minLength(1)
            // },
            requestJobNumber: {
                jobNumberFormat
            },
            requestDateRequestedDelivery: {
                required,
                minLength: minLength(1)
            },
            requestRushReason: {
                required,
                minLength: minLength(1)
            },
            requestSupervisorName: {
                required,
                minLength: minLength(1)
            },
            requestDirections: {
                required,
                minLength: minLength(1),
                notHtmlEditorDefault
            },
            requestSpecifications: {
                required,
                minLength: minLength(1),
                notHtmlEditorDefault
            },
            requestDeliveryInstructions: {
                required,
                minLength: minLength(1),
                notHtmlEditorDefault
            },
        },

        computed: {
            ...mapState({
                config_Departments: state => state.properties.jobDepartments,
                jobTeamSplit: state => state.properties.jobTeamSplit,

                currentUser: state => state.auth.currentUser,
                openState: state => state.ui.jobCreateOverlay.openState,

                job: state => state.jobs.job,
                jobAttachments: state => state.jobs.jobAttachments,

                createJobData: state => state.jobs.createJob_Data,

                isSaved: state => state.jobs.createJob_IsSaved,
                isSaving: state => state.jobs.createJob_IsSaving,

                milestoneOffset: state => state.properties.jobMilestoneDateOffsets,
                blackoutDates: state => state.properties.orgBlackoutDates,
                jobPriorities: state => state.properties.jobPriorities,
                jobInternationalSubdepartments: state => state.properties.jobInternationalSubdepartments,
                premediaRows: state => state.premedia.premediaRows,

                attachmentModel__Job: state => state.constants.SILA_FILE_ATTACHMENT_MODEL_JOB,
                fileUsageType__Job: state => state.constants.SILA_FILE_USAGE_TYPE_JOB_ATTACHMENT,


                premediaGroupIsEditing:state => state.premedia.premediaGroupIsEditing,
            }),

            ...mapGetters({
                currentJobId: 'getJobId',
                newJobData: 'getCreateJobData',
                findEnumItem: 'getJobEnumItem',
                createJobStatus: 'getCreateJobStatus',
                getJobEnumItemValue: 'getJobEnumItemValue',
                getQueuedFiles: 'getQueuedFiles',
                getUploadingFiles: 'getUploadingFiles',
                getProcessingFiles: 'getProcessingFiles',
                getCompletedFiles: 'getCompletedFiles',
                getAttachingFiles: 'getAttachingFiles',
            }),

            getTeamSplitContacts() {
                let ret = '';
                if (this.jobTeamSplit.team_lindsey_jobs.includes(this.requestType)) {
                    this.jobTeamSplit.team_lindsey_contacts.forEach(contact => {
                        ret += `<li>${contact.name} - <a href="mailto:${contact.email}" target="_blank">${contact.email}</a></li>`;
                    });
                }

                if (this.jobTeamSplit.team_darren_jobs.includes(this.requestType)) {
                    this.jobTeamSplit.team_darren_contacts.forEach(contact => {
                        ret += `<li>${contact.name} - <a href="mailto:${contact.email}" target="_blank">${contact.email}</a></li>`;
                    });
                }
                return ret
            },
            getTeamSplitContactsEmails() {
                let ret = '';
                if (this.jobTeamSplit.team_lindsey_jobs.includes(this.requestType)) {
                    this.jobTeamSplit.team_lindsey_contacts.forEach(contact => {
                        if (ret !=='') {
                            ret += ','
                        }
                        ret += contact.email;
                    });
                }

                if (this.jobTeamSplit.team_darren_jobs.includes(this.requestType)) {
                    this.jobTeamSplit.team_darren_contacts.forEach(contact => {
                        if (ret !=='') {
                            ret += ','
                        }
                        ret += contact.email;
                    });
                }
                return ret
            },

            fileUsageType() {
				return this.fileUsageType__Job
			},

			getAttachTo() {
                return {
                    model: this.attachmentModel__Job,
                    model_id: (this.createJobData && this.createJobData.id) ? this.createJobData.id : this.currentJobId,
                }
			},

            getMatchingEnum() {
                let self = this
                return (list, prop, val) => {
                    let m = self.findEnumItem(list, prop)
                    return (m) ? (m.name === val) : false
                }
            },

            today() {
                return dayjs().format('YYYY-MM-DD')
            },

            requestDateRequestedFirstComp_String() {
                let str = this.requestDateRequestedFirstComp
                return str ? str.substring(5, 7)+'/'+str.substring(8, 10)+'/'+str.substring(0, 4) : ''
            },

            requestDateRequestedDelivery_String() {
                let str = this.requestDateRequestedDelivery
                return str ? str.substring(5, 7)+'/'+str.substring(8, 10)+'/'+str.substring(0, 4) : ''
            },

            getScopedDepartments() {
                if (!this.currentUser) {
                    // Bail if not here (Logout will fail and then user session remains! Danger!!!)
                    return
                } else {
                    // Filter out inactive departments
                    let depts = this.config_Departments.filter(dept => (
                        dept.hasOwnProperty('active') === false || // there is no active prop set
                        (dept.hasOwnProperty('active') && dept.active === true) // tehre is an active prop, and it is true
                    ))

                    // If the user is an external ...
                    if (this.currentUser.access_type === SILA_USER_ACCESS_TYPE_EXTERNAL) {

                        // ... if they have no depts ...
                        if (this.currentUser.requestor_departments === null || !this.currentUser.requestor_departments) {
                            return [] // something is wrong on user, don't allow select any
                        } else {
                            // ... they do have depts and we can filter by it.
                            let userPerms = this.currentUser.permissions
                            let userDepts = this.currentUser.requestor_departments

                            // if JOB_CREATE
                            if (userPerms.map(perm => perm === SILA_PERMISSION_JOBS_JOB_CREATE)) {
                                // If SCOPE_DEPARTMENT get departments from user else return ALL departments
                                return userPerms.filter(perm => perm === SILA_PERMISSION_JOBS_JOB_CREATE_SCOPE_DEPARTMENT) ?
                                    depts.filter(dept => {
                                        if (userDepts.filter(uDept => uDept === dept.value).length > 0) return dept
                                    })
                                    : depts
                            }
                        }
                    } else {
                        // Is internal, and no need to filter further
                        return depts
                    }
                }

                return [] // fallback
            },

            fileAttachments() {
                // We use the "attachTo" model for files that are attached to a job and a comment
                // in order to filter the list returned. This used to just be the model App\JobComment
                //
                // If you are looking for where it comes from, we set it when we invoke the attachment
                // in src/components/ui/Attachments/JobsUploadDialog/Attachments_Jobs_Dialog.vue

                // Got it so comment id is always assigned now, woop!
                let files = new Array()
                let paramsWithId = null

                // Add in existing attachments if any exist
                if (this.jobAttachments.length > 0)
                    files = this.jobAttachments

                paramsWithId = {
                    attach_to: this.getFileAttachTo('JobAttachment')
                }

                files = files
                    // .concat(this.getCompletedFiles(paramsWithId))
                    // .concat(this.getUploadingFiles(paramsWithId))
                    // .concat(this.getProcessingFiles(paramsWithId))
                    .concat(this.getAttachingFiles(paramsWithId))
                    .concat(this.getQueuedFiles(paramsWithId))

                return files.sort((a, b) => a.addedToQueue - b.addedToQueue)
            },

            fileAttachmentsJustApprovals() {
                let ret = this.fileAttachments
                // filter out just approvals
                ret = ret.filter(item => item.file.usage_sub_type === 32);
                return ret
            },


            requestName: {
                get() {
                    return this.newJobData.name
                },
                set: debounce(function(val) {
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {name: val})
                }, DELAY)
            },
            requestDept: {
                get() {
                    return this.newJobData.department
                },
                set: debounce(function(val) {
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {department: val})
                }, DELAY)
            },
            request24Hr: {
                get() {
                    return this.newJobData.is_24_hour_rush
                },
                set: debounce(function(val) {
                    if (val) {
                        this.requestPriority = null
                    }
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {is_24_hour_rush: val})
                }, DELAY)
            },
            requestDeptOptions: {
                get() {
                    return this.newJobData.international_subdepartment
                },
                set: debounce(function(val) {
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {international_subdepartment: val})
                }, 50)
            },
            requestType: {
                get() {
                    return this.newJobData.type
                },
                set: debounce(function(val) {
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {type: val})
                }, 50)
            },
            requestJobNumber: {
                get() {
                    return this.newJobData.job_number != 'PENDING' ? this.newJobData.job_number : ''
                },
                set: debounce(function(val) {
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {job_number: val})
                }, DELAY)
            },
            requestDescription: {
                get() {
                    return this.newJobData.description
                },
                set: debounce(function(val) {
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {description: val})
                }, 500)
            },
            requestPriority: {
                get() {
                    return this.newJobData.priority
                },
                set: debounce(function(val) {
                    if (val != null) {
                        this.request24Hr = null
                    }
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {priority: val})
                }, DELAY)
            },
            requestDateRequestedFirstComp: {
                get() {
                    return (this.newJobData.priority_requested_first_comp_date)
                        ? this.newJobData.priority_requested_first_comp_date
                        : ""
                },
                set: debounce(function(val) {
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {priority_requested_first_comp_date: dayjs(val).format('YYYY-MM-DD HH:mm:ss')})
                }, DELAY)
            },
            requestDateRequestedDelivery: {
                get() {
                    return (this.newJobData.priority_requested_delivery_date)
                        ? this.newJobData.priority_requested_delivery_date
                        : ""
                },
                set: debounce(function(val) {
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {priority_requested_delivery_date: dayjs(val).format('YYYY-MM-DD HH:mm:ss')})
                }, DELAY)
            },
            requestRushReason: {
                get() {
                    return this.newJobData.priority_note
                },
                set: debounce(function(val) {
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {priority_note: val})
                }, DELAY)
            },
            requestSupervisorName: {
                get() {
                    return this.newJobData.priority_supervisor_name
                },
                set: debounce(function(val) {
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {priority_supervisor_name: val})
                }, DELAY)
            },
            requestDirections: {
                get() {
                    return this.newJobData.directions
                },
                set: debounce(function(val) {
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {directions: val})
                }, DELAY)
            },
            requestSpecifications: {
                get() {
                    return this.newJobData.specifications
                },
                set: debounce(function(val) {
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {specifications: val})
                }, DELAY)
            },
            requestDeliveryInstructions: {
                get() {
                    return this.newJobData.delivery_instructions
                },
                set: debounce(function(val) {
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {delivery_instructions: val})
                }, DELAY)
            },

            formIsEditable() {
                let bool = (this.job.state === 'draft' ||
                        this.job.state === 'print_not_approved' ||
                        this.job.state === 'package_not_approved' ||
                        this.job.state === 'exec_not_approved')

                return bool
            },

            isResubmit() {
                return (this.job.state === 'print_not_approved' || this.job.state === 'package_not_approved' || this.job.state === 'exec_not_approved') ? 'Resubmit Request' : 'Submit Request'
            },

            saveDeleteButtonLabel() {
                return (this.job.state === 'draft') ? 'Draft' : 'Request'
            },

            getSaveButtonDisabled() {
                if (this.premediaGroupIsEditing) {
                    return true
                } else {
                    if (this.formIsEditable) {
                        /* if(this.createJobStatus.hasChanged) {
                            return false
                        } */
                        return (this.createJobStatus.hasChanged) ? false : true
                    } else {
                        return true
                    }
                }
            },
            getGenericButtonsDisabled() {
                if (this.premediaGroupIsEditing) {
                    return true
                } else {
                    if (!this.createJobStatus.isSaved || this.createJobStatus.isSaving  || (!this.formIsEditable && this.isSaved)) {
                        return true
                    } else {
                        return false
                    }
                }

            },

            requestDepartmentIsDisabled () {
                // force type to be packaging
                return (this.requestType === SILA_JOB_TYPE_PACKAGING)
            }
        },

        mounted() {
            this.MB = new MilestoneBuilder(MILESTONEDATA, this.blackoutDates)
            // this.MB = new MilestoneBuilder(this.milestoneOffset, this.blackoutDates)
        },

        methods: {
            ...mapActions({
                saveNewJob: VUEX_JOB_CREATE_UPDATE,
                transitionJob: VUEX_JOB_TRANSITION,
                removeAttachment        : VUEX_JOB_REMOVE_FILE,
                removeAttachmentFromAQM : VUEX_ATTACHMENT_QUEUE_MANAGER_REMOVE_FILE,
                cancelRequest: VUEX_JOB_CREATE_CANCEL
            }),

            dropFiles(e) {
				this.uploadDragFile = false
				this.$refs.coreUploader.loadFiles(e.dataTransfer.files)
			},

			dropItemsLoaded() {
				this.addJobAttachment({
					// selectedType: 'upload',
                    // currentStep: 2,
                    showEditCompRound: false,
                    filteredUploadItemSubtypes: [
                        'SILA 1 Comp',
                        'SILA 1 Project File'
                    ]
				})
			},

			uploadDragOver(bool) {
				this.uploadDragFile = bool
			},

            cancelGlobalDialogue() {
                //GLOBAL ACTION PROMPT: Pass messages & dispatch action
                let actionObj = {
                    checkChangedFields: true,
                    title: '',
                    description: '',
                    dispatchAction: VUEX_JOB_CREATE_CANCEL,
                    dispatchActionObject: {}
                }
                this.$store.dispatch('VUEX_GLOBAL_ACTION_PROMPT_SHOW', {actionObj: actionObj})
            },

            ...mapMutations({
                clearDeliveryOptions: VUEX_JOB_CREATE_CLEAR_DELIVERY_OPTIONS,
                addJobAttachment: VUEX_ATTACHMENT_QUEUE_MANAGER_INIT_JOB_ATTACHMENT
            }),

            getMilestones() {
                let dept = this.findEnumItem('jobDepartments', this.requestDept)
                let type = this.findEnumItem('jobTypes', this.requestType)
                // let option = this.getJobEnumItemValue('jobInternationalSubdepartments', this.requestDeptOptions, 'name')
                let premedia = this.premediaRows.length > 0 ? true : false
                let rush24Hr = this.request24Hr
                let submittedDate = this.job.date_submitted

                this.milestones = []
                this.milestones = this.MB.getMilestones(dept, type, premedia, rush24Hr, submittedDate)
            },

            getAllowedDates(val) {
                const today = new Date();
                const tomorrow = new Date(today);
                tomorrow.setDate(today.getDate() + 1);
                const formattedToday = today.toISOString().substr(0, 10);
                const formattedTomorrow = tomorrow.toISOString().substr(0, 10);

                // Calculate the following Monday if tomorrow is a weekend
                let dayAfterTomorrow = new Date(tomorrow);
                if ([5, 6].includes(tomorrow.getDay())) { // if tomorrow is Saturday (5) or Sunday (6)
                    dayAfterTomorrow.setDate(tomorrow.getDate() + (8 - tomorrow.getDay()));
                }
                const formattedDayAfterTomorrow = dayAfterTomorrow.toISOString().substr(0, 10);

                // Check if the date is today, tomorrow, or the following Monday if tomorrow is a weekend
                if (val === formattedToday || val === formattedTomorrow || val === formattedDayAfterTomorrow) {
                    return false;
                }

                // Check if the date is in the blackoutDates array
                let blackout = this.blackoutDates.indexOf(val) === -1;

                // Check if the date is a weekend
                return ([5, 6].includes(new Date(val).getDay())) ? false : blackout;
            },



            submitJob() {
                this.clickedSubmitJobOnce = true
                this.getMilestones()

                let isValid = this.formValidation()
                this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {is_new_box: this.isNewBox})
                if (isValid) {
                    let transition
                    switch (this.newJobData.state) {
                        case 'print_not_approved' :
                            transition = 'resubmit_for_print_approval'
                            break

                        case 'package_not_approved' :
                            transition = 'resubmit_for_package_approval'
                            break

                        case 'exec_not_approved' :
                            transition = 'resubmit_for_exec_approval'
                            break

                        case '' :
                        default :
                            let isPrint = this.getMatchingEnum('jobTypes', this.requestType, 'Print')
                            let isPackage = this.getMatchingEnum('jobTypes', this.requestType, 'Packaging')
                            let isNewBox = (this.newJobData.is_new_box)
                            if (isPrint)  {
                                transition = 'submit_for_print_approval'
                            } else if (isPackage && isNewBox) {
                                transition = 'submit_for_package_approval'
                            } else {
                            transition = 'submit_for_exec_approval'
                            }

                            break
                    }

                    this.$nextTick(() => {
                        this.transitionJob({id: this.newJobData.id, transition: transition})
                    })
                } else {
                    this.isFormValid = false
                    this.submitted = true
                }
            },

            cancelJob() {
                this.submitted = false

                if (!this.formIsEditable) {
                    this.cancelRequest()
                } else {
                    this.cancelGlobalDialogue()
                }
            },

            saveJob() {
                this.submitted = false
                this.saveNewJob('isdraft')
            },

            /*
            /* Form Validations
            /*
            */
            formValidation() {
                // Form validation routines
                let priority_notes_isValid = (prop) => {
                    let first_comp, delivery, rush_reason, supervisor_name, hasApprovalFile
                    // define('SILA_JOB_PRIORITY_STANDARD',        1);
                    // define('SILA_JOB_PRIORITY_RUSH',          2);
                    // define('SILA_JOB_PRIORITY_EXTENDED',        3);
                    switch (prop) {
                        case 2: // Rush
                            //first_comp = this.$v.requestDateRequestedFirstComp.$invalid
                            hasApprovalFile = this.fileAttachmentsJustApprovals.length > 0
                            delivery = this.$v.requestDateRequestedDelivery.$invalid
                            rush_reason = this.$v.requestRushReason.$invalid
                            supervisor_name = this.$v.requestSupervisorName.$invalid
                            return (hasApprovalFile && !delivery && !rush_reason && !supervisor_name) ? true : false

                        case 3: // Extended
                            delivery = this.$v.requestDateRequestedDelivery.$invalid
                            return (!delivery) ? true : false

                        default :
                            return true
                    }
                }

                let rush24Hr_isValid = (prop) => {
                    if (prop) {
                        let hasApprovalFile = this.fileAttachmentsJustApprovals.length > 0
                        let supervisor_name = this.$v.requestSupervisorName.$invalid
                        let rush_reason = this.$v.requestRushReason.$invalid
                        return (hasApprovalFile && !supervisor_name && !rush_reason) ? true : false
                        // return (prop) ? (supervisor_name && rush_reason) ? true : false : true
                    } else {
                        return true
                    }
                }

                // Validation variable definitions
                let name = !this.$v.requestName.$invalid
                let description = !this.$v.requestDescription.$invalid && this.descriptionCount <= 5000
                let rush24hr = rush24Hr_isValid(this.request24Hr)
                let priority = priority_notes_isValid(this.requestPriority)
                let directions = !this.$v.requestDirections.$invalid
                let specifications = !this.$v.requestSpecifications.$invalid
                let instructions = !this.$v.requestDeliveryInstructions.$invalid
                // Validation check
                return (
                    name && description && priority && rush24hr &&
                    directions && specifications && instructions
                ) ? true : false
            },

            deleteJob() {
                //GLOBAL ACTION PROMPT: Pass messages & dispatch action
                let actionObj = {
                    checkChangedFields: false,
                    title: 'ARE YOU SURE?',
                    description: 'All data will be removed and can not be recovered.',
                    dispatchAction: VUEX_JOB_DELETE,
                    dispatchActionObject: {}
                }
                this.$store.dispatch('VUEX_GLOBAL_ACTION_PROMPT_SHOW', {actionObj: actionObj})
            },

            archiveJob() {
                this.submitted = false
                this.saveNewJob('isarchive')
            },

            // Broke this out to use in multiple place and later extend if needed
            // Possibly can use switch
            getFileAttachTo(type) {
                switch (type) {
                    case 'JobAttachment' :
                        return {
                            model_id: this.job.id,
                            model: SILA_FILE_ATTACHMENT_MODEL_JOB
                        }

                    default :
                        return {}
                }
            },

            removeFileAttachments(data) {
                if(data.attach_to) {
                    // remove newly attached items from AQM
                    let d = {
                        model    : data.attach_to.model,
                        model_id : data.attach_to.model_id,
                        file_id  : data.id
                    }
                    this.removeAttachmentFromAQM(d)
                } else {
                    // remove exiting items
                    let d = {
                        model    : data.model,
                        model_id : data.model_id,
                        file_id  : data.file_id
                    }
                   this.removeAttachment(d)
                }
            },

            pasteValues(type) {
                this.$nextTick(() => {
                    switch (type) {
                        case 'DESCRIPTION':
                            const descriptionHTML = document.querySelector("#requestDescription .ql-editor").innerHTML
                            this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {description: descriptionHTML})
                            break;
                        case 'DIRECTION':
                            const directHTML = document.querySelector("#requestDirections .ql-editor").innerHTML
                            this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {directions: directHTML})
                            break;
                        case 'JOB_SPECIFICATION':
                            const specificationHTML = document.querySelector("#requestSpecifications .ql-editor").innerHTML
                            this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {specifications: specificationHTML})
                            break;
                        case 'DELIVERY':
                            const deliveryHTML = document.querySelector("#requestDeliveryInstructions .ql-editor").innerHTML
                            this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {delivery_instructions: deliveryHTML})
                            break;
                    }
                })
            }
        },

        watch: {
            openState: {
                handler(val) {
                    if (val) {
                        this.submitted = false
                        this.getMilestones()
                        this.$refs.dialogContent.scrollTop = 0;
                    } else {
                        this.milestones = []
                    }
                }
            },
            milestones: {
                deep: true,
                handler(val) {
                    if (val) {
                        let data = {}
                        val.forEach(i => data[i.prop] = i.data)
                        this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', data)
                    }
                    this.$store.dispatch('VUEX_JOB_CREATE_INIT_MILESTONES_DONE')
                }
            },
            isNewBox: {
                handler(val) {
                    if (val) {
                        this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {job_number: ''}) //clear job number if checked
                    }
                    this.$store.dispatch('VUEX_JOB_CREATE_UPDATE_PROP', {is_new_box: val})
                }

            },
            requestDept: {
                handler(val) {
                    if (val === SILA_JOB_DEPARTMENT_INTERNATIONAL) {
                        this.requestDeptOptions = SILA_JOB_INTERNATIONAL_SUBDEPARTMENT_STANDARD
                    }
                    this.getMilestones()
                }
            },
            requestType: {
                handler(val) {
                    let n = [
                        SILA_JOB_TYPE_PRINT,
                        SILA_JOB_TYPE_PACKAGING,
                        SILA_JOB_TYPE_MULTIMEDIA,
                    ].includes(val)

                    if (n) {
                        this.request24Hr = false
                        this.canBe24Hr = false
                    } else {
                        this.canBe24Hr = true
                    }

                    this.clearDeliveryOptions()
                    this.getMilestones()

                    //reset isNewBox on job type change
                    this.isNewBox = (val===this.$store.state.constants.SILA_JOB_TYPE_PACKAGING)

                    // force department to be packaging
                    if (val === SILA_JOB_TYPE_PACKAGING) {
                        this.requestDept = SILA_JOB_DEPARTMENT_PACKAGING
                    }
                }
            },
            requestDeptOptions: {
                handler() {
                    this.getMilestones()
                }
            },
            request24Hr: {
                handler(val) {
                    this.getMilestones()
                }
            },
            premediaRows: {
                deep: true,
                handler() {
                    this.getMilestones()
                }
            },
            requestDescription: {
                deep: true,
                handler(val) {
                    this.descriptionCount = stripHtml(val).length || ''
                }
            }
        }
    }
</script>
